import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDuration, type AttendanceDay, type BreakProject, type TimeEntry } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { useUserProjects } from '@/hooks/queries';
import { ConfirmButton, ErrorNote, Modal, Spinner, SubmitButton } from './ui';
import { dayBounds, formatZonedTime, zonedTimeInput, zonedTimeToUtc } from '@/lib/tz';

/**
 * Admin view of one employee's day: every time entry, plus add / edit / delete.
 * This is how attendance gets corrected — log the hours that were actually
 * worked and the day re-classifies itself. Times are entered and shown in the
 * attendance timezone, whatever the admin's browser is set to.
 */
export function DayEntriesModal({
  userId,
  userName,
  day,
  timezone,
  breakProject,
  onClose,
}: {
  userId: string;
  userName: string;
  day: AttendanceDay;
  timezone: string;
  breakProject: BreakProject | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { start, end } = dayBounds(day.date, timezone);
  const entries = useQuery({
    queryKey: ['time-entries', 'day', userId, day.date],
    queryFn: () => apiFetch<{ items: TimeEntry[] }>('/api/time-entries', { query: { userId, from: start.toISOString(), to: end.toISOString(), pageSize: 100 } }),
  });
  const projects = useUserProjects(userId);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['time-entries'] });
    void qc.invalidateQueries({ queryKey: ['attendance'] });
    void qc.invalidateQueries({ queryKey: ['salary'] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/time-entries/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const date = new Date(`${day.date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  const projectOptions = [
    ...(projects.data ?? []).filter((p) => p.status === 'ACTIVE').map((p) => ({ id: p.id, name: p.name, color: p.color })),
    ...(breakProject && !(projects.data ?? []).some((p) => p.id === breakProject.id) ? [{ id: breakProject.id, name: `${breakProject.name} (break)`, color: breakProject.color }] : []),
  ];

  return (
    <Modal open wide title={`${userName} · ${date}`} onClose={onClose} footer={<button className="btn-secondary" onClick={onClose}>Done</button>}>
      <div className="mb-3 flex flex-wrap gap-4 text-sm text-slate-600">
        <span>Work <b className="font-mono">{formatDuration(day.workSeconds)}</b></span>
        <span>Break <b className="font-mono">{formatDuration(day.breakSeconds)}</b></span>
        <span className="capitalize">Status <b>{day.status.replace('-', ' ')}</b>{day.late ? ' · late' : ''}</span>
        <span className="ml-auto text-xs text-slate-400">Times in {timezone}</span>
      </div>

      <ErrorNote error={entries.error ?? remove.error} />

      {entries.isLoading ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : (entries.data?.items.length ?? 0) === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-sm text-slate-500">
          No time tracked on this day. Add the hours that were actually worked below.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {entries.data?.items.map((e) =>
            editing?.id === e.id ? (
              <li key={e.id} className="bg-slate-50 p-3">
                <EntryForm
                  key={e.id}
                  mode="edit"
                  entry={e}
                  day={day.date}
                  timezone={timezone}
                  projects={projectOptions}
                  userId={userId}
                  onDone={() => { setEditing(null); invalidate(); }}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li key={e.id} className="flex items-center gap-3 px-3 py-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: e.projectColor ?? '#cbd5e1' }} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-800">{e.projectName}{e.description ? <span className="text-slate-500"> — {e.description}</span> : null}</p>
                  <p className="text-xs tabular-nums text-slate-500">
                    {formatZonedTime(e.startTime, timezone)} – {e.endTime ? formatZonedTime(e.endTime, timezone) : 'running'}
                    {e.isManual && <span className="ml-2 badge bg-amber-100 text-amber-800">manual</span>}
                    <span className="ml-2 badge bg-slate-100 text-slate-600">{e.source.toLowerCase()}</span>
                  </p>
                </div>
                <span className="font-mono text-sm tabular-nums">{formatDuration(e.durationSeconds ?? 0)}</span>
                {e.endTime && <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setEditing(e)}>Edit</button>}
                <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Delete" confirmLabel="Confirm" onConfirm={() => remove.mutate(e.id)} />
              </li>
            ),
          )}
        </ul>
      )}

      <div className="mt-4 rounded-lg border border-brand-200 bg-brand-50/40 p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-800">Log hours</p>
        {projects.isLoading ? (
          <div className="flex justify-center py-3"><Spinner className="h-4 w-4" /></div>
        ) : projectOptions.length === 0 ? (
          <p className="text-sm text-slate-500">{userName} is not assigned to any project. Assign one under Projects first.</p>
        ) : (
          // Keyed on the option list too, so the default project is chosen from
          // the employee's real assignments rather than whatever loaded first.
          <EntryForm key={`new-${entries.data?.items.length ?? 0}-${projectOptions.map((p) => p.id).join(',')}`} mode="create" day={day.date} timezone={timezone} projects={projectOptions} userId={userId} onDone={invalidate} />
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">
        Entries you add or change here are marked manual and recorded in the audit log against your account.
      </p>
    </Modal>
  );
}

function EntryForm({
  mode,
  entry,
  day,
  timezone,
  projects,
  userId,
  onDone,
  onCancel,
}: {
  mode: 'create' | 'edit';
  entry?: TimeEntry;
  day: string;
  timezone: string;
  projects: { id: string; name: string; color: string }[];
  userId: string;
  onDone: () => void;
  onCancel?: () => void;
}) {
  // Default to a work project; the break project is offered but never assumed.
  const defaultProject = projects.find((p) => !/\(break\)$/.test(p.name)) ?? projects[0];
  const [projectId, setProjectId] = useState(entry?.projectId ?? defaultProject?.id ?? '');
  const [from, setFrom] = useState(entry ? zonedTimeInput(new Date(entry.startTime), timezone) : '09:00');
  const [to, setTo] = useState(entry?.endTime ? zonedTimeInput(new Date(entry.endTime), timezone) : '18:00');
  const [description, setDescription] = useState(entry?.description ?? '');

  const toInstant = (hhmm: string) => {
    const [y, m, d] = day.split('-').map(Number);
    const [h, min] = hhmm.split(':').map(Number);
    return zonedTimeToUtc(y, m, d, h, min, timezone);
  };

  const save = useMutation({
    mutationFn: () => {
      const startTime = toInstant(from);
      let endTime = toInstant(to);
      // An end before the start means the shift ran past midnight.
      if (endTime <= startTime) endTime = new Date(endTime.getTime() + 86400_000);
      const body = { projectId, description: description.trim() || null, startTime: startTime.toISOString(), endTime: endTime.toISOString() };
      return mode === 'edit' && entry
        ? apiFetch<TimeEntry>(`/api/time-entries/${entry.id}`, { method: 'PATCH', body })
        : apiFetch<TimeEntry>('/api/time-entries', { method: 'POST', body: { ...body, userId } });
    },
    onSuccess: onDone,
  });

  const preview = (() => {
    const s = toInstant(from);
    let e = toInstant(to);
    if (e <= s) e = new Date(e.getTime() + 86400_000);
    return formatDuration(Math.floor((e.getTime() - s.getTime()) / 1000));
  })();

  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="min-w-[10rem] flex-1">
        <label className="label">Project</label>
        <select className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label">From</label>
        <input type="time" className="input" value={from} onChange={(e) => setFrom(e.target.value)} required />
      </div>
      <div>
        <label className="label">To</label>
        <input type="time" className="input" value={to} onChange={(e) => setTo(e.target.value)} required />
      </div>
      <div className="min-w-[10rem] flex-1">
        <label className="label">Description <span className="font-normal text-slate-400">(optional)</span></label>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} placeholder="e.g. On-site, not tracked" />
      </div>
      <span className="pb-2 font-mono text-sm tabular-nums text-slate-600" title="Duration">{preview}</span>
      {onCancel && <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>}
      <SubmitButton pending={save.isPending} className="btn-primary">{mode === 'edit' ? 'Save' : 'Add'}</SubmitButton>
      {save.error && <div className="basis-full"><ErrorNote error={save.error} /></div>}
    </form>
  );
}
